import { forwardRef, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { getScheduleSettings } from '@elfsight/widget-conditional-display';
import { Timer, type TimerAPI, useTimerValue } from '@elfsight/widget-ui';
import { useLingui } from '@lingui/react/macro';
import { useSettings } from '@wu/widget-core';
import { useCountdownTimerState } from '../../../countdown-timer-state-context';
import { ElementBase } from '../element-base';
import { TimerTooltipTitle } from './timer-tooltip-title';
import type { TimerProps } from './types';

export const LibTimer = forwardRef<TimerAPI, TimerProps>(
  ({ onCountdownRestart, ...forwardingProps }, ref) => {
    const { t } = useLingui();

    const {
      hideBeforeCountdown,
      timerType,
      repeatTimerEnabled,
      remainingRestartUnit,
      remainingRestartValue,
      timerDuration,
      durationUnit,

      editMode,
      renderThumbnailMode,
      widgetId,
      ...settings
    } = useSettings();

    const schedule = getScheduleSettings(settings);
    // timerType не может быть number, но исключаем его чтоб TS не ругался
    const derivedTimerType = timerType === 'number' ? 'scheduled' : timerType;
    const { isPaused, value } = useTimerValue({
      ...schedule,
      repeatTimerEnabled,
      remainingRestartUnit,
      remainingRestartValue,
      timerDuration,
      durationUnit,
      editMode,
      widgetId,
      onTimerRestart: onCountdownRestart,
      id: 'countdown-timer',
      timerType: derivedTimerType
    });

    const { hideWidget } = useCountdownTimerState();

    useLayoutEffect(() => {
      const shouldHideWidget =
        isPaused && hideBeforeCountdown && !editMode && !renderThumbnailMode;
      if (shouldHideWidget) {
        hideWidget();
      }
    }, [
      isPaused,
      hideBeforeCountdown,
      hideWidget,
      editMode,
      renderThumbnailMode
    ]);

    return (
      <StyledTimer
        ref={ref}
        editMode={editMode}
        isPaused={isPaused || renderThumbnailMode}
        value={value}
        tooltipTitle={<TimerTooltipTitle />}
        withTooltip={!!editMode && hideBeforeCountdown}
        unitLabelTexts={{
          seconds: t`Seconds`,
          minutes: t`Minutes`,
          hours: t`Hours`,
          days: t`Days`
        }}
        {...forwardingProps}
      />
    );
  }
);

const StyledTimer = styled(ElementBase).attrs(({ theme: { timer } }) => ({
  ...timer,
  as: Timer
}))``;
